import { toast } from '@/hooks/use-toast';
import i18n from 'i18next';

const LEVEL_VARIANTS = {
  success: 'success',
  info: 'info',
  warning: 'warning',
  error: 'error',
  destructive: 'error',
};

export const NOTIFICATION_COPY = {
  'analytics.loadFailed': {
    level: 'error',
    title: 'Analytics could not load',
    fallbackDescription: 'Try refreshing this view in a moment.',
  },
  'brand.missingRequired': {
    level: 'warning',
    title: 'Required brand inputs are missing',
    description: 'Add a business description and target audience before saving.',
  },
  'brand.referenceMissing': {
    level: 'warning',
    title: 'Add a reference first',
    description: 'Upload an image or paste a public image URL.',
  },
  'brand.referenceSaved': {
    level: 'success',
    title: 'Reference saved',
  },
  'brand.referenceSaveFailed': {
    level: 'error',
    title: 'Reference was not saved',
    fallbackDescription: 'Try saving the reference again.',
  },
  'brand.referenceTooLarge': {
    level: 'warning',
    title: 'Reference is too large',
    description: 'Use an image up to 5 MB.',
  },
  'brand.saveFailed': {
    level: 'error',
    title: 'Brand inputs were not saved',
    fallbackDescription: 'Try saving again in a moment.',
  },
  'brand.saved': {
    level: 'success',
    title: 'Brand inputs saved',
    description: 'Your Content Plan will use the updated brand profile.',
  },
  'content.loadFailed': {
    level: 'error',
    title: 'Library could not load',
    fallbackDescription: 'Refresh the Library in a moment.',
  },
  'content.loadMoreFailed': {
    level: 'error',
    title: 'More posts could not load',
    fallbackDescription: 'Try loading more again.',
  },
  'contentPlan.brandLoadFailed': {
    level: 'error',
    title: 'Brand profile could not load',
    fallbackDescription: 'Refresh this page in a moment.',
  },
  'contentPlan.creditRequired': {
    level: 'warning',
    title: 'Plan quota needed',
    description: 'Plan quota is needed before this can continue.',
  },
  'contentPlan.generated': {
    level: 'success',
    title: 'Content Plan is ready',
    description: 'Platform adaptation has been refreshed.',
  },
  'contentPlan.generationFailed': {
    level: 'error',
    title: 'Content Plan was not generated',
    fallbackDescription: 'Try generating the plan again in a moment.',
  },
  'contentPlan.platformsRefreshed': {
    level: 'success',
    title: 'Platform plans refreshed',
    description: 'Each enabled channel now uses the latest Content Plan.',
  },
  'contentPlan.weekMissingPlatform': {
    level: 'warning',
    title: 'Pick a platform',
    description: 'Choose at least one enabled platform for the weekly plan.',
  },
  'contentPlan.weekMissingStrategy': {
    level: 'warning',
    title: 'Generate a Content Plan first',
    description: 'The weekly plan needs an active Content Plan.',
  },
  'contentPlan.weekReady': {
    level: 'success',
    title: 'Weekly plan is ready',
    description: 'The selected week has fresh post ideas.',
  },
  'contentPlan.weekFailed': {
    level: 'error',
    title: 'Weekly plan was not generated',
    fallbackDescription: 'Try generating the weekly plan again.',
  },
  'copy.failed': {
    level: 'error',
    title: 'Copy failed',
    description: 'Try copying again.',
  },
  'copy.success': {
    level: 'success',
    title: 'Copied',
    description: 'Text copied to clipboard.',
  },
  'post.imageRetryFailed': {
    level: 'error',
    title: 'Image was not generated',
    fallbackDescription: 'Try generating the image again.',
  },
  'post.imageRetryStarted': {
    level: 'success',
    title: 'Image generation started',
    description: 'The preview will update when the image is ready.',
  },
  'post.paidActionFailed': {
    level: 'error',
    title: 'Action could not be completed',
    fallbackDescription: 'Try again in a moment.',
  },
  'post.regenerateFailed': {
    level: 'error',
    title: 'Post was not regenerated',
    fallbackDescription: 'Try regenerating again in a moment.',
  },
  'post.regenerated': {
    level: 'success',
    title: 'Post regenerated',
    description: 'A new version is ready to review.',
  },
  'post.rescheduleFailed': {
    level: 'error',
    title: 'Publish time was not changed',
    fallbackDescription: 'Try saving the new time again.',
  },
  'post.rescheduled': {
    level: 'success',
    title: 'Publish time updated',
    description: 'Scheduler now has the new publish time.',
  },
  'post.retryFailed': {
    level: 'error',
    title: 'Publish retry did not start',
    fallbackDescription: 'Try retrying again in a moment.',
  },
  'post.retryStarted': {
    level: 'success',
    title: 'Publishing retry started',
    description: 'Scheduler will update as soon as the retry finishes.',
  },
  'post.saveFailed': {
    level: 'error',
    title: 'Draft was not saved',
    fallbackDescription: 'Try saving the draft again.',
  },
  'post.savedDraft': {
    level: 'success',
    title: 'Draft saved',
    description: 'A new version is ready in the Library.',
  },
  'post.scheduleFailed': {
    level: 'error',
    title: 'Post was not scheduled',
    fallbackDescription: 'Try scheduling again in a moment.',
  },
  'publishing.platformSaveFailed': {
    level: 'error',
    title: '{platform} was not updated',
    fallbackDescription: 'Try changing the platform settings again.',
  },
  'publishing.platformSaved': {
    level: 'success',
    title: '{platform} updated',
  },
  'publishing.saveFailed': {
    level: 'error',
    title: 'Publishing preferences were not saved',
    fallbackDescription: 'Try saving again in a moment.',
  },
  'publishing.saved': {
    level: 'success',
    title: 'Publishing preferences saved',
    description: 'Future plans will use the updated cadence.',
  },
  'scheduler.cloneFailed': {
    level: 'error',
    title: 'Clone was not created',
    fallbackDescription: 'Try creating the clone again.',
  },
  'scheduler.cloned': {
    level: 'success',
    title: 'Clone created',
    description: 'The new post is now in Scheduler.',
  },
  'scheduler.deleteFailed': {
    level: 'error',
    title: 'Post was not deleted',
    fallbackDescription: 'Try deleting it again.',
  },
  'scheduler.deleted': {
    level: 'success',
    title: 'Post deleted',
    description: 'It has been removed from Scheduler.',
  },
  'scheduler.loadFailed': {
    level: 'error',
    title: 'Scheduler could not load',
    fallbackDescription: 'Try refreshing Scheduler in a moment.',
  },
  'scheduler.rescheduleFailed': {
    level: 'error',
    title: 'Publish time was not changed',
    fallbackDescription: 'Try saving the new time again.',
  },
  'scheduler.rescheduled': {
    level: 'success',
    title: 'Publish time updated',
    description: 'Scheduler now has the new time.',
  },
  'scheduler.retryFailed': {
    level: 'error',
    title: 'Retry did not start',
    fallbackDescription: 'Try retrying again.',
  },
  'scheduler.retryStarted': {
    level: 'success',
    title: 'Publishing retry started',
    description: 'Scheduler will update when publishing finishes.',
  },
  'scheduler.unscheduleFailed': {
    level: 'error',
    title: 'Post was not unscheduled',
    fallbackDescription: 'Try unscheduling again.',
  },
  'scheduler.unscheduled': {
    level: 'success',
    title: 'Post unscheduled',
    description: 'It has been removed from the calendar.',
  },
};

export function notify(keyOrOptions, options = {}) {
  return toast(notificationProps(keyOrOptions, options));
}

export function notificationProps(keyOrOptions, options = {}) {
  const source = typeof keyOrOptions === 'string'
    ? NOTIFICATION_COPY[keyOrOptions] || {}
    : keyOrOptions || {};
  const merged = { ...source, ...options };
  const level = normalizeNotificationLevel(merged.level || merged.type || merged.variant || 'info');
  const values = merged.values || {};
  const description = merged.error
    ? friendlyErrorMessage(merged.error, merged.description || merged.fallbackDescription || source.fallbackDescription || 'Try again in a moment.')
    : merged.description;
  const { error, fallbackDescription, type, values: _values, ...toastProps } = merged;

  return {
    ...toastProps,
    level,
    variant: LEVEL_VARIANTS[level],
    title: formatTemplate(merged.title || 'Notice', values),
    description: formatTemplate(description, values),
  };
}

export function notificationText(key, options = {}) {
  const props = notificationProps(key, options);
  return props.description || props.title;
}

export function friendlyErrorMessage(error, fallback = 'Try again in a moment.') {
  const code = String(error?.code || error?.details?.reason || '').toUpperCase();
  const message = String(error?.message || '').trim();
  const status = Number(error?.status || 0);

  if (code === 'PLAN_LAPSED') return i18n.t('paymentIssue.lapsedBody');

  if (/CREDIT|INSUFFICIENT|QUOTA/.test(code) || /credit|quota/i.test(message)) {
    return 'Plan quota is needed before this can continue.';
  }
  if (status === 401 || status === 403) {
    return 'Your session needs a refresh. Sign in again and retry.';
  }
  if (status === 404 || code.includes('NOT_FOUND')) {
    return 'I could not find that item. Refresh and try again.';
  }
  if (status === 409 || code.includes('CONFLICT')) {
    return 'This changed somewhere else. Refresh and try again.';
  }
  if (status === 429 || code.includes('RATE_LIMIT')) {
    return 'Dathent is busy right now. Try again in a moment.';
  }
  if (status === 0 || code === 'NETWORK_ERROR') {
    return 'Check your connection and try again.';
  }
  if (status >= 500) {
    return fallback;
  }

  const clean = cleanProductError(message);
  return clean || fallback;
}

function normalizeNotificationLevel(value) {
  const level = LEVEL_VARIANTS[String(value || '').toLowerCase()];
  return level || 'info';
}

function cleanProductError(message) {
  if (!message) return '';
  if (/\b(http|trace|worker|provider|backend|schema|sql|d1|api key|endpoint|internal|unknown_error)\b/i.test(message)) return '';
  if (/api request failed/i.test(message)) return '';
  return message.replace(/\s+/g, ' ').trim();
}

function formatTemplate(value, values) {
  if (typeof value !== 'string') return value;
  return value.replace(/\{([a-zA-Z0-9_]+)\}/g, (_, key) => {
    const next = values[key];
    return next === undefined || next === null ? '' : String(next);
  });
}
